/**
 * Utility functions for determining application qualifications
 */

/**
 * Determines the qualification type based on application data
 * @param application The application data
 * @returns The qualification type (Fachkraft/Fachkraft in Anerkennung/Azubi/Ausreisepflichtig/Hilfskraft)
 */
export const getQualificationType = (application: any): string => {
  // Helper function to check if a date is in the past
  const isDateExpired = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    return date < today;
  };

  // 4. "Ausreisepflichtig" - Check first as it's highest priority
  // "Visum gültig bis" oder "Aufenthaltstitel gültig bis" abgelaufen, also Datum in Vergangenheit
  if (
    isDateExpired(application.visa_validity) ||
    isDateExpired(application.residence_permit_validity)
  ) {
    return "Ausreisepflichtig";
  }

  // 1. "Fachkraft"
  // IF Aufenthaltstitel Status = "Erteilt" AND Aufenthaltstitel = "Anerkannte Fachkraft"
  // OR Visum Status = "Erteilt" AND Visum = "Anerkannte Fachkraft"
  // OR Berufsanerkennung Status = "Anerkannt"
  if (
    (application.residence_permit_status === "granted" &&
      application.residence_permit_type === "recognized_skilled_worker") ||
    (application.visa_application_status === "granted" &&
      application.visa_status === "anerkannte_fachkraft") ||
    application.recognition_status === "approved"
  ) {
    return "Fachkraft";
  }

  // 2. "Fachkraft in Anerkennung"
  // IF Aufenthaltstitel Status = "Erteilt" AND Aufenthaltstitel = "Fachkraft in Anerkennung" OR "Hilfskraft während der Anerkennung"
  // OR Visum Status = "Erteilt" AND Visum = "Fachkraft in Anerkennung" OR "Hilfskraft während der Anerkennung"
  // OR "Berufsanerkennung Status" = "Beantragt" oder "Teilweise anerkannt"
  if (
    (application.residence_permit_status === "granted" &&
      (application.residence_permit_type === "skilled_worker_recognition" ||
        application.residence_permit_type ===
          "auxiliary_worker_recognition")) ||
    (application.visa_application_status === "granted" &&
      (application.visa_status === "fachkraft_anerkennung" ||
        application.visa_status === "hilfskraft")) ||
    application.recognition_status === "pending" ||
    application.recognition_status === "partially_approved"
  ) {
    return "Fachkraft in Anerkennung";
  }

  // 3. "Azubi"
  // IF Aufenthaltstitel Status = "Erteilt" AND Aufenthaltstitel = "Berufsausbildung"
  // OR Visum Status = "Erteilt" AND Visum = "Berufsausbildung"
  // OR Pflegeausbildung Status = "In Ausbildung"
  if (
    (application.residence_permit_status === "granted" &&
      application.residence_permit_type === "vocational_training") ||
    (application.visa_application_status === "granted" &&
      application.visa_status === "ausbildung") ||
    application.nursing_education_status === "in_progress"
  ) {
    return "Azubi";
  }

  // 5. "Hilfskraft" - Fallback
  return "Hilfskraft";
};
